/**
 * The casing job picker's labels, sort and search — shared by the Clock In
 * screen's job sheet and the Home clock card's optional job picker, so both
 * order and name jobs the same way. Moved verbatim out of ClockInScreen.tsx.
 */
import { jobLabel } from './job-label';
import { getJobTypeLabel } from '../constants/job-types';
import { formatCalendarDate, parseInstantMs } from './calendar-date';
import { PHASE_WORD, handPhases, handSortDate, phaseDateValue, phaseMismatch, type FieldPhase } from './job-phase-dates';

// ─── Job picker rows ─────────────────────────────────────────────
//
// A row used to read `J-0072` over `Completed` and nothing else, which no hand
// could match to the job they were standing on. It now leads with what they
// know the job by — the operator and the rig — and carries the number, type
// and day underneath. Every field is already on GET /jobs (`SELECT j.*` plus
// `customer_name`), read camelCase ?? snake_case.

export const pickStr = (job: any, camel: string, snake: string): string | null => {
  const v = job?.[camel] ?? job?.[snake];
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
};

/**
 * `rig_name`, plus `rig_number` when it adds something.
 *
 * Measured on Smith 2026-10-06: `rig_name` holds the whole rig ("Ensign 144")
 * on 42 of 43 jobs and `rig_number` is NULL on all 43. So the number is
 * appended only when the name does not already contain it as a word — the
 * row must not read "Ensign 144 144" on a tenant that fills both.
 */
export function pickerRig(job: any): string | null {
  const name = pickStr(job, 'rigName', 'rig_name');
  const num = pickStr(job, 'rigNumber', 'rig_number');
  if (name && num) {
    const escaped = num.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|\\W)${escaped}(\\W|$)`, 'i').test(name) ? name : `${name} ${num}`;
  }
  return name ?? num;
}

/**
 * Line 1: `Customer · Rig`, as two parts so the row can truncate the customer
 * and keep the rig whole. At 375px "Vermilion Energy Inc. · Ensign 144" does
 * not fit, and on Smith every job is Vermilion's — the rig is the half that
 * tells the rows apart, so it is the half that must survive.
 *
 * One half missing renders `—` in its place, so the shape holds. Both missing
 * falls back to the well, then the LSD or city, then the job number — a row is
 * never blank and never "undefined".
 */
export function pickerTitle(job: any): { lead: string; rig: string | null } {
  const customer = pickStr(job, 'customerName', 'customer_name');
  const rig = pickerRig(job);
  if (customer || rig) return { lead: customer ?? '—', rig: rig ?? '—' };
  return {
    lead: pickStr(job, 'wellName', 'well_name')
      ?? pickStr(job, 'wellIdLsd', 'well_id_lsd')
      ?? pickStr(job, 'locationCity', 'location_city')
      ?? pickStr(job, 'jobNumber', 'job_number')
      ?? '—',
    rig: null,
  };
}

/**
 * Line 2: `J-0072 · Production · Wash Nov 4` — or `· Wash Nov 4 · Run Nov 9`
 * for a hand on both phases.
 *
 * The day is THE HAND'S phase date (`my_phase`, api#1039): a wash-only hand
 * reads the wash day, a run-only hand the run day. It printed
 * `scheduled_date` (the run) for everyone — TNT J-4404, a wash hand picking a
 * job dated five days after their wash. A single-phase hand on a job without
 * that phase gets their phase em-dashed, never the other phase's day.
 *
 * Both are job WALL-CLOCK columns (see calendar-date.ts), read as written with
 * `formatCalendarDate`, never converted through a zone.
 */
export function pickerMeta(job: any): string {
  const number = pickStr(job, 'jobNumber', 'job_number') ?? '—';
  const type = getJobTypeLabel(job?.jobType ?? job?.job_type);
  const myPhase = job?.myPhase ?? job?.my_phase;
  const mismatch = phaseMismatch(job, myPhase);
  const phases: FieldPhase[] = mismatch ? [mismatch] : handPhases(job, myPhase);
  const days = phases.map(
    (p) => `${PHASE_WORD[p]} ${formatCalendarDate(phaseDateValue(job, p), { month: 'short', day: 'numeric' }) ?? '—'}`,
  );
  return [number, type, ...days].join(' · ');
}

/**
 * Active → On Hold → Scheduled/Pending → Completed → Cancelled. Anything the
 * enum has not heard of sorts last rather than borrowing a real bucket.
 */
export const PICKER_RANK: Record<string, number> = {
  active: 0, on_hold: 1, scheduled: 2, pending: 2, completed: 3, cancelled: 4,
};

/**
 * Upcoming work soonest first — by the hand's own phase date (a wash-only
 * hand's wash day, not the run's) with undated jobs after the dated ones;
 * completed work newest `completed_at` first. Within active and on-hold the
 * API's own order (newest created) stands — Array.prototype.sort is stable.
 */
export function sortPickerJobs(list: any[]): any[] {
  const rank = (j: any) => PICKER_RANK[j?.status] ?? 5;
  const ms = (j: any, camel: string, snake: string) => parseInstantMs(j?.[camel] ?? j?.[snake]);
  return [...list].sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    if (rank(a) === 2) {
      // The hand's own next day on the job — see `handSortDate`.
      const x = parseInstantMs(handSortDate(a, a?.myPhase ?? a?.my_phase));
      const y = parseInstantMs(handSortDate(b, b?.myPhase ?? b?.my_phase));
      if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
      return x - y;
    }
    if (rank(a) === 3) {
      const x = ms(a, 'completedAt', 'completed_at');
      const y = ms(b, 'completedAt', 'completed_at');
      if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
      return y - x;
    }
    return 0;
  });
}

/**
 * Everything a hand might type: what the row shows (customer, rig, number)
 * plus the well, LSD and city it may not show, and the old `jobLabel` so the
 * `J-4313 · CV 106` form still finds the job.
 */
export function pickerSearchText(job: any): string {
  return [
    jobLabel(job),
    pickStr(job, 'jobNumber', 'job_number'),
    pickStr(job, 'customerName', 'customer_name'),
    pickerRig(job),
    pickStr(job, 'wellName', 'well_name'),
    pickStr(job, 'wellIdLsd', 'well_id_lsd'),
    pickStr(job, 'locationCity', 'location_city'),
  ].filter(Boolean).join(' ').toLowerCase();
}

/**
 * The picker's source (api#1039): GET /timesheets/clock-in-jobs, the caller's
 * own crewed, still-open jobs. One key, so the Clock In screen and the Home
 * clock card share one cache entry.
 */
export const CLOCK_IN_JOBS_KEY = ['timesheets', 'clock-in-jobs'] as const;

/** Shown when a casing hand choosing FIELD has no job to pick. Operator wording. */
export const NO_ASSIGNED_JOBS_TEXT = 'No jobs assigned to you — contact dispatch.';
